import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { API_BASE_URL } from "../config/api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { socket, connectSocket } from "../socket";
import NotificationService from "../services/notificationService";
import PatientService from "../services/patientService";
import { preventFocusScroll, withScrollPreservedStateUpdate } from "../utils/preservePageScroll";
import {
  resolveNotificationMessage,
  resolveNotificationTitle,
} from "../utils/notificationI18n";
import { getPanelNotificationColors } from "../design-system/panel/panelNotificationColors";
import { PANEL } from "../design-system/panel/panelTokens";
import {
  panelDropdownTriggerSx,
  panelTopbarActionIconSx,
  panelDropdownPopoverSlotProps,
  panelDropdownPopoverModalProps,
  panelDropdownHeaderSx,
  panelDropdownHeaderTitleSx,
  panelDropdownHeaderMetaSx,
  panelDropdownHeaderActionSx,
  panelDropdownListSx,
  panelDropdownItemRowSx,
  panelDropdownItemButtonSx,
  panelDropdownItemIconWrapSx,
  panelDropdownItemTitleSx,
  panelDropdownItemBodySx,
  panelDropdownItemTimeSx,
  panelDropdownDividerSx,
  panelDropdownEmptySx,
  panelDropdownEmptyIconSx,
  panelDropdownEmptyTitleSx,
  panelDropdownEmptyHintSx,
  panelDropdownBadgeSx,
  panelDropdownUnreadDotSx,
} from "../design-system/panel/panelDropdownSx";
import {
  buildObserverStudyIntentFromNotification,
  buildObserverStudyLocation,
} from "../scenes/mediavision/utils/observerStudyIntent";

function normalizeNotification(raw, fallbackId) {
  return {
    id: raw.id || fallbackId,
    type: raw.type || "info",
    title: raw.title || "",
    message: raw.message || "",
    data: raw.data || {},
    read_at: raw.read_at ?? null,
    created_at: raw.created_at || new Date().toISOString(),
  };
}

function formatRelativeTime(isoDate, locale, t) {
  if (!isoDate) return "";
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return t("timeJustNow");
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t("timeMinutesAgo", { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t("timeHoursAgo", { count: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return t("timeDaysAgo", { count: diffDay });
  return new Date(isoDate).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function notificationIcon(type, colors, isPanel = false) {
  const sx = {
    fontSize: 20,
    color: isPanel ? PANEL.accent.main : colors.greenAccent[400],
  };
  if (type?.includes("form") || type?.includes("filled")) {
    return <AssignmentTurnedInOutlinedIcon sx={sx} />;
  }
  if (type?.includes("patient")) {
    return <PersonOutlineOutlinedIcon sx={sx} />;
  }
  return <InfoOutlinedIcon sx={sx} />;
}

/* ── Export Job helpers ── */
function exportJobIcon(status) {
  if (status === "completed") return <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#10b981" }} />;
  if (status === "failed") return <ErrorOutlineIcon sx={{ fontSize: 20, color: "#ef4444" }} />;
  return <HourglassEmptyIcon sx={{ fontSize: 20, color: "#f59e0b" }} />;
}

function exportJobStatusLabel(status, t) {
  if (status === "completed") return t("exportCompleted", "Tamamlandı");
  if (status === "failed") return t("exportFailed", "Hata Oluştu");
  if (status === "processing") return t("exportProcessing", "İşleniyor...");
  return t("exportPending", "Bekliyor...");
}

function exportJobStatusColor(status) {
  if (status === "completed") return "#10b981";
  if (status === "failed") return "#ef4444";
  return "#f59e0b";
}

export default function TopbarNotificationsMenu({ colors, variant = "theme" }) {
  const isPanel = variant === "panel";
  const resolvedColors =
    isPanel ? getPanelNotificationColors() : colors;
  const palette = resolvedColors;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const notificationService = useMemo(() => new NotificationService(), []);
  const patientService = useMemo(() => new PatientService(), []);
  const localIdRef = useRef(0);

  const [notifications, setNotifications] = useState([]);
  const [exportJobs, setExportJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, message: "" });

  const locale = i18n.language?.startsWith("tr")
    ? "tr-TR"
    : i18n.language?.startsWith("fr")
    ? "fr-FR"
    : "en-US";

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at).length,
    [notifications],
  );

  const activeExportJobsCount = useMemo(
    () => exportJobs.filter((j) => j.status === "pending" || j.status === "processing").length,
    [exportJobs],
  );

  const totalBadgeCount = unreadCount + activeExportJobsCount;

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationService.list({ limit: 30 });
      const rows = (data.notifications || []).map((n) => normalizeNotification(n, n.id));
      setNotifications(rows);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [notificationService]);

  const loadExportJobs = useCallback(async () => {
    try {
      const data = await patientService.getExportJobs();
      if (data && data.jobs) {
        setExportJobs(data.jobs);
      }
    } catch (err) {
      // Sessizce geç — endpoint çalışmıyorsa bildirimler yine de gösterilir
      console.error("Failed to fetch export jobs:", err);
    }
  }, [patientService]);

  useEffect(() => {
    loadNotifications();
    loadExportJobs();
  }, [loadNotifications, loadExportJobs]);

  // Aktif export job varsa, durumlarını poll et
  useEffect(() => {
    if (activeExportJobsCount === 0) return;
    const interval = setInterval(() => {
      loadExportJobs();
    }, 10000);
    return () => clearInterval(interval);
  }, [activeExportJobsCount, loadExportJobs]);

  useEffect(() => {
    // Bağlantı Topbar tarafından doktor kimliğiyle de kurulur; burada tekrar
    // çağrılması güvenlidir ve bildirim menüsü tek başına render edildiğinde
    // bağlantının kurulmasını garanti eder.
    connectSocket();

    const handleNotification = (data) => {
      const id = data.id || `socket-${++localIdRef.current}`;
      const normalized = normalizeNotification(data, id);

      setNotifications((prev) => {
        const withoutDup = prev.filter((n) => n.id !== normalized.id);
        return [normalized, ...withoutDup].slice(0, 50);
      });

      setSnackbarInfo({
        open: true,
        message:
          resolveNotificationMessage(normalized, t) ||
          resolveNotificationTitle(normalized, t) ||
          t("newNotification"),
      });
    };

    socket.on("notification", handleNotification);
    return () => {
      socket.off("notification", handleNotification);
    };
  }, [t]);

  const handleOpen = withScrollPreservedStateUpdate((event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
    loadExportJobs();
  });

  const handleClose = () => setAnchorEl(null);

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
      );
    } catch (error) {
      console.error("markAllRead failed:", error);
    }
  };

  const deleteAll = async () => {
    try {
      await notificationService.deleteAll();
      setNotifications([]);
    } catch (error) {
      console.error("deleteAll failed:", error);
    }
  };

  const deleteOne = async (event, notif) => {
    event.stopPropagation();
    event.preventDefault();

    const isPersisted = notif.id && !String(notif.id).startsWith("socket-");
    if (isPersisted) {
      try {
        await notificationService.deleteOne(notif.id);
      } catch (error) {
        console.error("deleteOne failed:", error);
        return;
      }
    }
    setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
  };

  const handleItemClick = async (notif) => {
    if (!notif.read_at && notif.id && !String(notif.id).startsWith("socket-")) {
      try {
        await notificationService.markRead(notif.id);
      } catch (error) {
        console.error("markRead failed:", error);
      }
    }
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notif.id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n,
      ),
    );
    handleClose();
    if (notif.data?.patientId) {
      navigate(`/patients/${notif.data.patientId}`);
      return;
    }
    if (notif.data?.formId) {
      navigate(`/forms/${notif.data.formId}`);
      return;
    }
    if (notif.data?.entityType === "ml_project") {
      const tab = notif.data?.mlProjectsTab || "shared";
      navigate(`/machine-learning?mlProjectsTab=${encodeURIComponent(tab)}`);
      return;
    }
    const observerLoc = buildObserverStudyLocation(buildObserverStudyIntentFromNotification(notif));
    if (observerLoc) {
      navigate(observerLoc.pathname, { state: observerLoc.state });
    }
  };

  const handleExportDownload = async (event, jobId) => {
    event.stopPropagation();
    try {
      const { downloadExportJob } = await import("../utils/authDownload");
      await downloadExportJob(jobId);
    } catch (err) {
      console.error("Export download failed", err);
    }
  };

  const open = Boolean(anchorEl);

  const hasAnyContent = notifications.length > 0 || exportJobs.length > 0;

  return (
    <>
      <Tooltip title={t("notifications")}>
        <IconButton
          type="button"
          onClick={handleOpen}
          onFocus={preventFocusScroll}
          aria-label={t("notifications")}
          sx={
            isPanel
              ? { ...panelDropdownTriggerSx, position: "relative" }
              : {
                  backgroundColor: `${palette.blueAccent[700]}30`,
                  border: `1px solid ${palette.blueAccent[500]}40`,
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: `${palette.blueAccent[600]}50`,
                  },
                }
          }
        >
          <Badge
            badgeContent={totalBadgeCount}
            color="error"
            max={99}
            invisible={totalBadgeCount === 0}
            sx={isPanel ? panelDropdownBadgeSx : {
              "& .MuiBadge-badge": {
                fontWeight: 700,
                fontSize: "0.65rem",
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <NotificationsOutlinedIcon sx={isPanel ? panelTopbarActionIconSx : { color: palette.blueAccent[300] }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        {...(isPanel ? panelDropdownPopoverModalProps : { disableScrollLock: true, BackdropProps: { invisible: true } })}
        slotProps={isPanel ? panelDropdownPopoverSlotProps(380) : undefined}
        PaperProps={
          isPanel
            ? undefined
            : {
                sx: {
                  mt: 1.5,
                  width: { xs: "min(92vw, 380px)", sm: 380 },
                  borderRadius: 2.5,
                  overflow: "hidden",
                  backgroundColor: palette.primary[400],
                  border: `1px solid ${palette.blueAccent[700]}55`,
                  boxShadow: `0 12px 40px rgba(0,0,0,0.28), 0 0 0 1px ${palette.blueAccent[500]}22`,
                },
              }
        }
      >
        <Box
          sx={
            isPanel
              ? panelDropdownHeaderSx
              : {
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  background: `linear-gradient(135deg, ${palette.primary[500]} 0%, ${palette.blueAccent[900]}88 100%)`,
                  borderBottom: `1px solid ${palette.blueAccent[700]}44`,
                }
          }
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={
                isPanel
                  ? panelDropdownHeaderTitleSx
                  : { fontWeight: 700, color: palette.grey[100], lineHeight: 1.2 }
              }
            >
              {t("notifications")}
            </Typography>
            {totalBadgeCount > 0 && (
              <Typography
                variant="caption"
                sx={isPanel ? panelDropdownHeaderMetaSx : { color: palette.greenAccent[400], fontWeight: 600 }}
              >
                {unreadCount > 0 && t("unreadNotificationsCount", { count: unreadCount })}
                {unreadCount > 0 && activeExportJobsCount > 0 && " · "}
                {activeExportJobsCount > 0 && t("activeExportJobs", { count: activeExportJobsCount, defaultValue: `${activeExportJobsCount} aktif indirme` })}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            {unreadCount > 0 && (
              <Tooltip title={t("markAllNotificationsRead")}>
                <IconButton
                  size="small"
                  onClick={markAllRead}
                  aria-label={t("markAllNotificationsRead")}
                  sx={isPanel ? panelDropdownHeaderActionSx : {
                    color: palette.blueAccent[200],
                    border: `1px solid ${palette.blueAccent[400]}55`,
                    borderRadius: 1.5,
                    p: 0.75,
                    "&:hover": { backgroundColor: `${palette.blueAccent[700]}44` },
                  }}
                >
                  <DoneAllOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Tooltip title={t("deleteAllNotifications")}>
                <IconButton
                  size="small"
                  onClick={deleteAll}
                  aria-label={t("deleteAllNotifications")}
                  sx={
                    isPanel
                      ? {
                          ...panelDropdownHeaderActionSx,
                          "&:hover": {
                            backgroundColor: PANEL.danger.soft,
                            color: PANEL.danger.main,
                            borderColor: PANEL.danger.main,
                          },
                        }
                      : {
                          color: palette.redAccent?.[300] || "#f28b82",
                          border: `1px solid ${palette.redAccent?.[500] || "#e57373"}55`,
                          borderRadius: 1.5,
                          p: 0.75,
                          "&:hover": { backgroundColor: `${palette.redAccent?.[900] || "#5c1a1a"}44` },
                        }
                  }
                >
                  <DeleteSweepOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={isPanel ? panelDropdownListSx : { maxHeight: 400, overflowY: "auto" }}>
          {/* ── Export Jobs Section ── */}
          {exportJobs.length > 0 && (
            <>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.68rem",
                    color: isPanel ? PANEL.text.label : palette.blueAccent?.[200] || "#94a3b8",
                  }}
                >
                  <FileDownloadOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                  {t("exports", "İndirmeler")}
                </Typography>
              </Box>
              {exportJobs.map((job, index) => {
                const jobTime = job.created_at || job.createdAt;
                return (
                  <Box key={`export-${job.id}`}>
                    <Box sx={isPanel ? panelDropdownItemRowSx(job.status === "pending" || job.status === "processing") : {
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      backgroundColor: (job.status === "pending" || job.status === "processing") ? `${palette.greenAccent?.[900] || "#064e3b"}33` : "transparent",
                      borderLeft: (job.status === "pending" || job.status === "processing")
                        ? "3px solid #f59e0b"
                        : job.status === "completed"
                        ? "3px solid #10b981"
                        : job.status === "failed"
                        ? "3px solid #ef4444"
                        : "3px solid transparent",
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: isPanel ? undefined : `${palette.blueAccent?.[800] || "#1e3a5f"}44`,
                      },
                    }}>
                      <Box
                        sx={isPanel ? {
                          ...panelDropdownItemButtonSx,
                          cursor: "default",
                        } : {
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          pr: 0.5,
                          textAlign: "left",
                        }}
                      >
                        <Box sx={isPanel ? panelDropdownItemIconWrapSx(exportJobStatusColor(job.status)) : {
                          mt: 0.25,
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: `${exportJobStatusColor(job.status)}18`,
                          border: `1px solid ${exportJobStatusColor(job.status)}44`,
                        }}>
                          {exportJobIcon(job.status)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={isPanel ? panelDropdownItemTitleSx(job.status !== "completed" && job.status !== "failed") : {
                              fontWeight: 600,
                              color: isPanel ? PANEL.text.primary : palette.grey?.[100] || "#fff",
                              lineHeight: 1.35,
                              mb: 0.35,
                            }}
                          >
                            {t("advancedExcelExport", "Gelişmiş Excel Aktarımı")}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: exportJobStatusColor(job.status),
                              fontWeight: 600,
                              lineHeight: 1.45,
                            }}
                          >
                            {exportJobStatusLabel(job.status, t)}
                            {(job.status === "pending" || job.status === "processing") && (
                              <span style={{ display: "inline-flex", alignItems: "center" }}>
                                {typeof job.progress === "number" && job.progress > 0 && job.progress < 100 && (
                                  <span style={{ marginLeft: "4px", fontSize: "0.95em", opacity: 0.95 }}>
                                    (%{job.progress})
                                  </span>
                                )}
                                <CircularProgress size={10} sx={{ ml: 0.75, color: "#f59e0b" }} />
                              </span>
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={isPanel ? panelDropdownItemTimeSx : {
                              color: isPanel ? PANEL.text.label : palette.blueAccent?.[200] || "#94a3b8",
                              mt: 0.5,
                              display: "block",
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              opacity: 0.9,
                            }}
                          >
                            {formatRelativeTime(jobTime, locale, t)}
                          </Typography>
                        </Box>
                        {job.status === "completed" && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleExportDownload(e, job.id)}
                            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              mt: 0.5,
                              flexShrink: 0,
                              alignSelf: "center",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              borderRadius: "8px",
                              borderColor: "#10b981",
                              color: "#10b981",
                              "&:hover": {
                                borderColor: "#059669",
                                backgroundColor: "#10b98118",
                              },
                            }}
                          >
                            {t("download", "İndir")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                    {(index < exportJobs.length - 1 || notifications.length > 0) && (
                      <Divider sx={isPanel ? panelDropdownDividerSx : { borderColor: `${palette.grey?.[800] || "#334155"}40`, mx: 2 }} />
                    )}
                  </Box>
                );
              })}
              {notifications.length > 0 && (
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.68rem",
                      color: isPanel ? PANEL.text.label : palette.blueAccent?.[200] || "#94a3b8",
                    }}
                  >
                    <NotificationsOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                    {t("notifications")}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* ── Notifications Section ── */}
          {loading && notifications.length === 0 && exportJobs.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress
                size={28}
                sx={{ color: isPanel ? PANEL.accent.main : palette.greenAccent[400] }}
              />
            </Box>
          ) : !hasAnyContent ? (
            <Box sx={isPanel ? panelDropdownEmptySx : { textAlign: "center", px: 3, py: 5 }}>
              <NotificationsNoneOutlinedIcon
                sx={
                  isPanel
                    ? panelDropdownEmptyIconSx
                    : { fontSize: 44, color: palette.blueAccent[300], mb: 1.5, opacity: 0.85 }
                }
              />
              <Typography
                variant="body2"
                align="center"
                sx={
                  isPanel
                    ? panelDropdownEmptyTitleSx
                    : { fontWeight: 600, color: palette.grey[100], mb: 0.75, lineHeight: 1.4 }
                }
              >
                {t("noNotifications")}
              </Typography>
              <Typography
                variant="caption"
                align="center"
                sx={
                  isPanel
                    ? panelDropdownEmptyHintSx
                    : {
                        color: palette.blueAccent[200],
                        lineHeight: 1.55,
                        display: "block",
                        fontSize: "0.8rem",
                        opacity: 0.92,
                      }
                }
              >
                {t("notificationsEmptyHint")}
              </Typography>
            </Box>
          ) : (
            notifications.map((notif, index) => {
              const unread = !notif.read_at;
              const displayTitle = resolveNotificationTitle(notif, t);
              const displayMessage = resolveNotificationMessage(notif, t);
              return (
                <Box key={notif.id}>
                  <Box sx={isPanel ? panelDropdownItemRowSx(unread) : {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    backgroundColor: unread ? `${palette.greenAccent[900]}33` : "transparent",
                    borderLeft: unread
                      ? `3px solid ${palette.greenAccent[500]}`
                      : "3px solid transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: `${palette.blueAccent[800]}44`,
                    },
                  }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleItemClick(notif)}
                      sx={isPanel ? panelDropdownItemButtonSx : {
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        pr: 0.5,
                        textAlign: "left",
                        border: "none",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                        color: "inherit",
                      }}
                    >
                    <Box sx={isPanel ? panelDropdownItemIconWrapSx(PANEL.accent.main) : {
                        mt: 0.25,
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: `${palette.blueAccent[800]}55`,
                        border: `1px solid ${palette.blueAccent[600]}44`,
                      }}
                    >
                      {notificationIcon(notif.type, palette, isPanel)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={isPanel ? panelDropdownItemTitleSx(unread) : {
                          fontWeight: unread ? 700 : 600,
                          color: palette.grey[100],
                          lineHeight: 1.35,
                          mb: 0.35,
                        }}
                      >
                        {displayTitle}
                      </Typography>
                      {displayMessage && (
                        <Typography
                          variant="caption"
                          sx={isPanel ? panelDropdownItemBodySx : {
                            color: palette.grey[400],
                            display: "block",
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayMessage}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={isPanel ? panelDropdownItemTimeSx : {
                          color: palette.blueAccent[200],
                          mt: 0.5,
                          display: "block",
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          opacity: 0.9,
                        }}
                      >
                        {formatRelativeTime(notif.created_at, locale, t)}
                      </Typography>
                    </Box>
                    {unread && (
                      isPanel ? (
                        <Box sx={panelDropdownUnreadDotSx} />
                      ) : (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: palette.greenAccent[500],
                            flexShrink: 0,
                            mt: 0.75,
                            boxShadow: `0 0 6px ${palette.greenAccent[500]}88`,
                          }}
                        />
                      )
                    )}
                    </Box>
                    <Tooltip title={t("deleteNotification")}>
                      <IconButton
                        size="small"
                        type="button"
                        onClick={(event) => deleteOne(event, notif)}
                        aria-label={t("deleteNotification")}
                        sx={{
                          mt: 1,
                          mr: 1,
                          flexShrink: 0,
                          color: isPanel ? PANEL.text.label : palette.grey[500],
                          "&:hover": {
                            color: isPanel ? PANEL.danger.main : palette.redAccent?.[300] || "#f28b82",
                            backgroundColor: isPanel
                              ? PANEL.danger.soft
                              : `${palette.redAccent?.[900] || "#5c1a1a"}33`,
                          },
                        }}
                      >
                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {index < notifications.length - 1 && (
                    <Divider sx={isPanel ? panelDropdownDividerSx : { borderColor: `${palette.grey[800]}40`, mx: 2 }} />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>

      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={5000}
        onClose={() => setSnackbarInfo((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="info"
          onClose={() => setSnackbarInfo((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
    </>
  );
}
